import { useEffect, useMemo } from 'react';

import { GearbeltLoader } from '@gearbelt/ui/icons';
import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';

import { GbmwRedirectLoader } from '@/components/gbmw-redirect-loader';
import { env } from '@/env';
import { getAWACurrentSitemateUserQueryOptions, getAWAMigrationStatusQueryOptions, getAWAUserQueryOptions, getAWAWorkspaceQueryOptions } from '@/queries/awa';
import { AWAApiV2Service } from '@/services/awa-api-v2';
import { useAWAStore } from '@/stores/awa.store';
import { checkIfUserProfileComplete, pathIndicatesInspectionFlow, stripAWAPath } from '@/utils/awa';

// Bounce a migrated mechanic to GBMW. `replace` so AWA isn't left in history.
function GbmwRedirect() {
  useEffect(() => {
    if (env.VITE_GBMW_URL) {
      window.location.replace(env.VITE_GBMW_URL);
    }
  }, []);

  return <GbmwRedirectLoader />;
}

function AWARoute() {
  const location = useLocation();
  const { setWorkspace, authTokens } = useAWAStore();

  const [searchParams] = useSearchParams();
  const { workspaceId: workspaceIdParam } = useParams();
  const workspaceIdSearchParam = searchParams.get('workspaceId');
  const rawRedirect = searchParams.get('rawRedirect');

  const workspaceId = useMemo(() => workspaceIdParam || workspaceIdSearchParam, [workspaceIdParam, workspaceIdSearchParam]);
  const redirectTo = searchParams.get('redirectTo');

  // Users here to submit an inspection — directly, via prestart, or from a QR code
  // (which lands on the asset / qr-assign pages) — are never prompted or redirected.
  // Also honour the pending `redirectTo`, so the exemption survives the auth +
  // onboarding hops (`/auth/onboarding`) whose pathname has lost the QR context.
  const isInspectionSubmissionFlow = pathIndicatesInspectionFlow(location.pathname) || pathIndicatesInspectionFlow(redirectTo);

  const getRedirectUrl = (path: string) => {
    const baseUrl = `${path}?workspaceId=${workspaceId}&redirectTo=${stripAWAPath(location.pathname, true)}`;
    return rawRedirect ? `${baseUrl}&rawRedirect=${rawRedirect}` : baseUrl;
  };

  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useQuery({
    ...getAWAUserQueryOptions(),
    enabled: !!authTokens?.accessToken,
  });

  const { isLoading: isWorkspaceLoading } = useQuery({
    ...getAWAWorkspaceQueryOptions({ workspaceId }),
    queryFn: async () => {
      if (!workspaceId) throw new Error('Workspace ID is required');
      const { workspace } = await (await AWAApiV2Service.GetPublicWorkspaceById(workspaceId)).json();
      setWorkspace(workspace);
      return workspace;
    },
    enabled: !!workspaceId && !!user,
  });

  // The Gearbelt-side account (null email = not yet a member) is the authoritative
  // promotion signal — distinct from the Sitemate profile above, whose email is a
  // placeholder for phone-only users.
  const { data: gearbeltAccount, isLoading: isGearbeltAccountLoading } = useQuery({
    ...getAWACurrentSitemateUserQueryOptions(),
    enabled: !!authTokens?.accessToken && !!user,
  });

  // A mechanic who migrated by LINKING to an existing email account has no member
  // email on THIS (phone) identity, so the gearbeltAccount.email check below can't
  // see it. The migration record — keyed by their original `sub`, not the phone — is
  // the authoritative "already migrated" signal; when set, route them to GBMW rather
  // than re-prompt. Only fetched on the one path where it can change the outcome.
  const shouldCheckMigrationStatus = !!authTokens?.accessToken && !!gearbeltAccount && !gearbeltAccount.email && !isInspectionSubmissionFlow;

  const { data: migrationStatus, isLoading: isMigrationStatusLoading } = useQuery({
    ...getAWAMigrationStatusQueryOptions(),
    enabled: shouldCheckMigrationStatus,
  });

  if (!workspaceId || workspaceId === 'undefined' || workspaceId === 'null') {
    return (
      <div className="flex h-[100dvh] flex-col items-center justify-center">
        <div className="text-2xl font-medium">Invalid URL</div>
      </div>
    );
  }

  if (isUserLoading || isWorkspaceLoading || isGearbeltAccountLoading || (shouldCheckMigrationStatus && isMigrationStatusLoading)) {
    return (
      <div className="flex h-[100dvh] flex-col items-center justify-center">
        <GearbeltLoader size={50} />
        <div className="text-secondary-text mt-2 text-sm">Loading Gearbelt...</div>
      </div>
    );
  }

  if (!authTokens?.accessToken) {
    return <Navigate to={getRedirectUrl('/auth/login')} />;
  }

  if (user && !isUserLoading && !location.pathname.startsWith('/auth/onboarding')) {
    if (!checkIfUserProfileComplete(user)) {
      return <Navigate to={getRedirectUrl('/auth/onboarding')} />;
    }
  }

  // Once the profile is complete, key off the Gearbelt account (not the Sitemate
  // profile, whose email is a placeholder for phone-only users). Members enter via
  // the workspace-redirect route, so they're caught here before reaching the
  // management surfaces; `/auth/email-setup` lives outside this guard, so the
  // migrate redirect can't loop.
  if (gearbeltAccount && !isInspectionSubmissionFlow) {
    if (gearbeltAccount.email || migrationStatus?.hasMigrated) {
      // Already a member — on this identity (email set) or via a LINK to another
      // identity (migration record). Send them to GBMW, where members belong.
      if (env.VITE_GBMW_URL) {
        return <GbmwRedirect />;
      }
    } else {
      return <Navigate to={getRedirectUrl('/auth/email-setup')} />;
    }
  }

  if (!user) {
    // Auth potentially refreshing token, so we don't want to render anything as pages might call other endpoints
    return <></>;
  }

  if (isUserError) {
    return <Navigate to={getRedirectUrl('/auth/login')} />;
  }

  return <Outlet />;
}

export default AWARoute;
